import { useEffect, useRef, useState } from "react";
import api, { formatApiError, API, getToken } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ImageSquare, FloppyDisk, Buildings, FileDoc, Download, Copy, FileArrowDown, FileArrowUp, Clock } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import LetterheadVersionsPanel from "@/components/LetterheadVersionsPanel";
import LetterheadDesigner from "@/components/LetterheadDesigner";

/**
 * Stammdaten – Mandanten-Stammdaten (Admin-only):
 *   – Marke & Logo
 *   – Firmenangaben (inkl. Bank + Steuer)
 *   – Impressum / Rechtliches (§5 TMG)
 *   – Eigentümerreport-Auto-Versand
 *
 * Klar getrennt von den persönlichen Einstellungen (SettingsDialog) und
 * der allgemeinen Mandanten-Konfiguration unter /einstellungen.
 */
export default function Stammdaten() {
  const { tenant, refresh } = useAuth();
  const [form, setForm] = useState({
    company_name: "", company_street: "", company_zip: "", company_city: "",
    company_phone: "", company_email: "", company_web: "",
    brand: "", tagline: "", signature_name: "", signature_role: "",
    bank_name: "", bank_bic: "", bank_iban: "",
    tax_id: "", vat_id: "", customer_number: "",
    legal_representative: "", register_court: "", register_number: "",
    regulatory_authority: "", professional_title: "",
    professional_chamber: "", professional_country: "Deutschland",
    // 2026-08 — Regional-Formate für Datum/Uhrzeit (Admin-only)
    regional_date_format: "DD.MM.YYYY",
    regional_time_format: "HH:MM",
  });
  const [logoBuster, setLogoBuster] = useState(0);
  const [histOpen, setHistOpen] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    const { data } = await api.get("/settings/company");
    setForm((s) => ({ ...s, ...data }));
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put("/settings/company", form);
      toast.success("Stammdaten gespeichert");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Logo zu groß (max. 5 MB)");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await fetch(`${API}/tenants/me/logo`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      });
      if (!r.ok) throw new Error(`Upload fehlgeschlagen: ${r.status}`);
      toast.success("Logo aktualisiert");
      setLogoBuster(Date.now());
      e.target.value = "";
      await refresh();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const logoSrc = tenant?.logo_url
    ? `${tenant.logo_url.startsWith("http") ? tenant.logo_url : `${process.env.REACT_APP_BACKEND_URL || ""}${tenant.logo_url}`}${tenant.logo_url.includes("?") ? "&" : "?"}t=${logoBuster}`
    : null;

  return (
    <div className="space-y-5 max-w-4xl" data-testid="stammdaten-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Stammdaten</h1>
        <p className="text-sm text-slate-500 mt-1">
          Mandanten-Stammdaten: Firmenangaben, Logo, Geschäftspapier und Eigentümerreport-Versand.
        </p>
      </div>

      <Tabs defaultValue={new URLSearchParams(window.location.search).get("tab") || "brand"}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="brand" data-testid="stamm-tab-brand">Marke & Logo</TabsTrigger>
          <TabsTrigger value="paper" data-testid="stamm-tab-paper">Firmenangaben</TabsTrigger>
          <TabsTrigger value="letterhead" data-testid="stamm-tab-letterhead">Geschäftspapier</TabsTrigger>
          <TabsTrigger value="impressum" data-testid="stamm-tab-impressum">Impressum / Rechtliches</TabsTrigger>
          <TabsTrigger value="reports" data-testid="stamm-tab-reports">Eigentümerreport</TabsTrigger>
          <TabsTrigger value="addin" data-testid="stamm-tab-addin">Word Add-In</TabsTrigger>
          <TabsTrigger value="migration" data-testid="stamm-tab-migration">Daten-Übertragung</TabsTrigger>
        </TabsList>

        <TabsContent value="brand" className="pt-4 space-y-4">
          <div className="bg-white border border-slate-200 rounded-md p-4 sm:p-6 shadow-sm">
            <div className="text-sm font-semibold mb-3 flex items-center gap-2"><ImageSquare size={16} /> Logo</div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="w-40 h-20 border border-slate-200 rounded-md bg-slate-50 flex items-center justify-center overflow-hidden">
                {logoSrc ? (
                  <img src={logoSrc} alt="Logo" className="max-h-full max-w-full object-contain p-2" />
                ) : (
                  <Buildings size={28} className="text-slate-400" />
                )}
              </div>
              <div className="space-y-2">
                <input type="file" ref={fileRef} accept="image/*" className="hidden" onChange={uploadLogo} data-testid="stamm-logo-file-input" />
                <Button onClick={() => fileRef.current?.click()} className="bg-slate-900 hover:bg-slate-800" data-testid="stamm-upload-logo-btn">
                  Logo hochladen
                </Button>
                <p className="text-xs text-slate-500">PNG / SVG / JPG, max. 5 MB. <strong>Empfohlene Abmessungen:</strong> 600 × 200 px (3:1) für Querformat-Logo oder 200 × 200 px (1:1) für Wort-Bild-Marke. Transparenter Hintergrund bevorzugt — das Logo wird auf max. 36 px Höhe in der Anwendung skaliert.</p>
              </div>
            </div>
          </div>

          <form onSubmit={save} className="bg-white border border-slate-200 rounded-md p-4 sm:p-6 shadow-sm space-y-3">
            <div className="text-sm font-semibold">Markenbezeichnung</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <F label="Anzeigename / Marke"><Input value={form.brand} onChange={(e) => set("brand", e.target.value)} data-testid="stamm-brand-input" /></F>
              <F label="Slogan / Tagline"><Input value={form.tagline} onChange={(e) => set("tagline", e.target.value)} /></F>
            </div>
            <div className="flex justify-end">
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800 gap-2" data-testid="stamm-save-brand-btn">
                <FloppyDisk size={16} /> Speichern
              </Button>
            </div>
          </form>
        </TabsContent>

        <TabsContent value="paper" className="pt-4">
          <form onSubmit={save} className="bg-white border border-slate-200 rounded-md p-4 sm:p-6 shadow-sm space-y-3">
            <div className="text-sm font-semibold">Firmenangaben</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <F label="Firmenname" className="sm:col-span-2"><Input required value={form.company_name} onChange={(e) => set("company_name", e.target.value)} data-testid="stamm-company-name" /></F>
              <F label="Straße + Hausnr."><Input value={form.company_street} onChange={(e) => set("company_street", e.target.value)} /></F>
              <F label="Stadt"><Input value={form.company_city} onChange={(e) => set("company_city", e.target.value)} /></F>
              <F label="PLZ"><Input value={form.company_zip} onChange={(e) => set("company_zip", e.target.value)} /></F>
              <F label="Telefon"><Input value={form.company_phone} onChange={(e) => set("company_phone", e.target.value)} /></F>
              <F label="E-Mail"><Input type="email" value={form.company_email} onChange={(e) => set("company_email", e.target.value)} /></F>
              <F label="Website"><Input value={form.company_web} onChange={(e) => set("company_web", e.target.value)} /></F>
            </div>

            <div className="text-sm font-semibold pt-2">Unterschriftsblock</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <F label="Name (Unterschrift)"><Input value={form.signature_name} onChange={(e) => set("signature_name", e.target.value)} /></F>
              <F label="Rolle / Position"><Input value={form.signature_role} onChange={(e) => set("signature_role", e.target.value)} /></F>
            </div>

            <div className="text-sm font-semibold pt-2">Bank & Steuer</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <F label="Bank"><Input value={form.bank_name || ""} onChange={(e) => set("bank_name", e.target.value)} data-testid="stamm-bank-name" /></F>
              <F label="BIC"><Input value={form.bank_bic || ""} onChange={(e) => set("bank_bic", e.target.value)} /></F>
              <F label="IBAN" className="sm:col-span-2"><Input value={form.bank_iban || ""} onChange={(e) => set("bank_iban", e.target.value)} data-testid="stamm-bank-iban" /></F>
              <F label="Steuernummer"><Input value={form.tax_id || ""} onChange={(e) => set("tax_id", e.target.value)} /></F>
              <F label="USt-IdNr."><Input value={form.vat_id || ""} onChange={(e) => set("vat_id", e.target.value)} placeholder="DE123456789" data-testid="stamm-vat-id" /></F>
              <F label="Kundennummer (Plattform)"><Input value={form.customer_number || ""} onChange={(e) => set("customer_number", e.target.value)} data-testid="stamm-customer-number" /></F>
            </div>

            <div className="flex justify-between items-center pt-2">
              <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={() => setHistOpen(true)} data-testid="stamm-history-btn">
                <Clock size={14} /> Änderungsverlauf
              </Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800 gap-2" data-testid="stamm-save-paper-btn">
                <FloppyDisk size={16} /> Speichern
              </Button>
            </div>
          </form>
        </TabsContent>

        <TabsContent value="letterhead" className="pt-4">
          <Tabs defaultValue="designer">
            <TabsList>
              <TabsTrigger value="designer" data-testid="lh-tab-designer">Designer</TabsTrigger>
              <TabsTrigger value="versions" data-testid="lh-tab-versions">Versionen</TabsTrigger>
            </TabsList>
            <TabsContent value="designer" className="pt-3">
              <LetterheadDesigner />
            </TabsContent>
            <TabsContent value="versions" className="pt-3">
              <LetterheadVersionsPanel />
            </TabsContent>
          </Tabs>
        </TabsContent>

        <TabsContent value="impressum" className="pt-4">
          <form onSubmit={save} className="bg-white border border-slate-200 rounded-md p-4 sm:p-6 shadow-sm space-y-3">
            <div className="text-sm font-semibold">Pflichtangaben gemäß § 5 TMG</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <F label="Vertretungsberechtigte(r) / Geschäftsführer(in)" className="sm:col-span-2">
                <Input value={form.legal_representative || ""} onChange={(e) => set("legal_representative", e.target.value)} placeholder="z. B. Max Mustermann" data-testid="stamm-legal-rep" />
              </F>
              <F label="Registergericht">
                <Input value={form.register_court || ""} onChange={(e) => set("register_court", e.target.value)} placeholder="z. B. Amtsgericht Berlin" data-testid="stamm-register-court" />
              </F>
              <F label="Registernummer (HRB)">
                <Input value={form.register_number || ""} onChange={(e) => set("register_number", e.target.value)} placeholder="HRB 123456" data-testid="stamm-register-number" />
              </F>
            </div>

            <div className="text-sm font-semibold pt-3">Berufsrechtliche Angaben (Immobilienverwalter §34c GewO)</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <F label="Berufsbezeichnung">
                <Input value={form.professional_title || ""} onChange={(e) => set("professional_title", e.target.value)} placeholder="z. B. Immobilienverwalter" data-testid="stamm-prof-title" />
              </F>
              <F label="Verleihungsstaat">
                <Input value={form.professional_country || "Deutschland"} onChange={(e) => set("professional_country", e.target.value)} data-testid="stamm-prof-country" />
              </F>
              <F label="Kammer">
                <Input value={form.professional_chamber || ""} onChange={(e) => set("professional_chamber", e.target.value)} placeholder="z. B. IHK Berlin" data-testid="stamm-prof-chamber" />
              </F>
              <F label="Zuständige Aufsichtsbehörde">
                <Input value={form.regulatory_authority || ""} onChange={(e) => set("regulatory_authority", e.target.value)} placeholder="z. B. Bezirksamt Charlottenburg-Wilmersdorf" data-testid="stamm-reg-authority" />
              </F>
            </div>

            <p className="text-[11px] text-slate-500 leading-relaxed pt-2">
              Hinweis: Diese Angaben werden im Impressum Ihres Eigentümerportals und auf
              ausgehenden Schreiben verwendet.
            </p>

            {/* 2026-08 — Regional-Formate (Admin-only) */}
            <div className="pt-4 mt-4 border-t border-slate-200 space-y-2" data-testid="stamm-regional-section">
              <div className="text-sm font-semibold">Regionale Formate</div>
              <p className="text-[11px] text-slate-500 leading-relaxed">
                Bestimmt, wie Datums- und Uhrzeit-Angaben in Schreiben, PDFs und der Oberfläche dargestellt werden.
                Standard sind die europäischen Formate.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <F label="Datumsformat">
                  <select
                    value={form.regional_date_format || "DD.MM.YYYY"}
                    onChange={(e) => set("regional_date_format", e.target.value)}
                    data-testid="stamm-regional-date-format"
                    className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                  >
                    <option value="DD.MM.YYYY">31.10.2026 &nbsp; (deutsch/europäisch)</option>
                    <option value="D. MMMM YYYY">31. Oktober 2026 &nbsp; (Langform)</option>
                    <option value="YYYY-MM-DD">2026-10-31 &nbsp; (ISO 8601)</option>
                    <option value="DD/MM/YYYY">31/10/2026 &nbsp; (britisch)</option>
                    <option value="MM/DD/YYYY">10/31/2026 &nbsp; (US)</option>
                  </select>
                </F>
                <F label="Uhrzeitformat">
                  <select
                    value={form.regional_time_format || "HH:MM"}
                    onChange={(e) => set("regional_time_format", e.target.value)}
                    data-testid="stamm-regional-time-format"
                    className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                  >
                    <option value="HH:MM">14:30 &nbsp; (24 h)</option>
                    <option value="HH:MM Uhr">14:30 Uhr &nbsp; (24 h mit „Uhr")</option>
                    <option value="hh:mm AM/PM">02:30 PM &nbsp; (12 h)</option>
                  </select>
                </F>
              </div>
            </div>

            <div className="flex justify-between items-center pt-2">
              <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={() => setHistOpen(true)} data-testid="stamm-history-btn-2">
                <Clock size={14} /> Änderungsverlauf
              </Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800 gap-2" data-testid="stamm-save-impressum-btn">
                <FloppyDisk size={16} /> Impressum speichern
              </Button>
            </div>
          </form>
        </TabsContent>

        <TabsContent value="reports" className="pt-4 space-y-4">
          <AutoReportSection />
        </TabsContent>

        <TabsContent value="addin" className="pt-4 space-y-4">
          <WordAddInTab />
        </TabsContent>

        <TabsContent value="migration" className="pt-4 space-y-4">
          <MigrationTab />
        </TabsContent>
      </Tabs>

      <StammdatenHistoryDialog open={histOpen} onOpenChange={setHistOpen} />
    </div>
  );
}

function StammdatenHistoryDialog({ open, onOpenChange }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (!open) return;
    api.get("/settings/company/history")
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]));
  }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="stamm-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Clock size={18} /> Änderungsverlauf Unternehmensstammdaten</DialogTitle>
          <DialogDescription>Wer hat wann welche Stammdaten-Felder geändert?</DialogDescription>
        </DialogHeader>
        {items.length === 0 ? (
          <div className="py-12 text-center text-sm text-slate-500">Noch keine Änderungen protokolliert.</div>
        ) : (
          <ol className="space-y-3">
            {items.map((e, i) => {
              const diff = e.details?.diff || {};
              const fields = e.details?.changed_fields || [];
              return (
                <li key={i} className="border border-slate-200 rounded-md p-3 bg-white" data-testid={`stamm-hist-entry-${i}`}>
                  <div className="flex items-start justify-between gap-3 mb-1.5">
                    <div className="text-sm">
                      <span className="font-medium text-slate-900">{e.user_name || "—"}</span>
                      <span className="text-slate-400 text-xs ml-2">{e.user_email || ""}</span>
                    </div>
                    <div className="text-[11px] text-slate-500 font-mono whitespace-nowrap">{new Date(e.created_at).toLocaleString("de-DE")}</div>
                  </div>
                  {fields.length === 0 ? (
                    <div className="text-xs text-slate-500 italic">Keine getrackten Änderungen</div>
                  ) : (
                    <ul className="space-y-1 text-xs">
                      {fields.map((f) => (
                        <li key={f} className="font-mono bg-slate-50 border border-slate-100 rounded px-2 py-1">
                          <span className="text-slate-500">{f}:</span>{" "}
                          <span className="text-rose-700 line-through">{String(diff[f]?.old || "—").slice(0, 40)}</span>
                          <span className="mx-1 text-slate-400">→</span>
                          <span className="text-emerald-700 font-medium">{String(diff[f]?.new || "—").slice(0, 40)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ol>
        )}
      </DialogContent>
    </Dialog>
  );
}

function WordAddInTab() {
  const manifestUrl = `${API}/addin/manifest.xml`;
  const copy = () => {
    navigator.clipboard.writeText(manifestUrl);
    toast.success("Link kopiert");
  };
  return (
    <>
      <div className="bg-white border border-slate-200 rounded-md p-4 sm:p-6 shadow-sm space-y-3">
        <div className="text-sm font-semibold flex items-center gap-2"><FileDoc size={16} /> Domivus Word Add-In</div>
        <p className="text-sm text-slate-600">
          Mit dem Word Add-In erstellen Sie Schreiben direkt in Microsoft Word, fügen Kontakte/Objekte/
          Mietverhältnisse als Platzhalter ein, nutzen Textbausteine und speichern das fertige Dokument
          mit einem Klick zurück in Domivus (verknüpft mit dem ausgewählten Empfänger).
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 items-end">
          <div>
            <Label className="text-xs">Manifest-URL</Label>
            <Input readOnly value={manifestUrl} className="font-mono text-xs" data-testid="addin-manifest-url" />
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={copy} className="gap-2" type="button"><Copy size={14} /> Kopieren</Button>
            <a href={manifestUrl} download="domivus-addin-manifest.xml">
              <Button className="bg-slate-900 hover:bg-slate-800 gap-2" type="button" data-testid="addin-download-btn">
                <Download size={14} /> Manifest herunterladen
              </Button>
            </a>
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-4 sm:p-6 shadow-sm">
        <div className="text-sm font-semibold mb-3">So installieren Sie das Add-In in Word</div>
        <ol className="space-y-3 text-sm text-slate-700">
          <Step n="1" title="Manifest herunterladen">
            Klicken Sie oben auf „Manifest herunterladen". Die Datei <span className="font-mono text-xs">domivus-addin-manifest.xml</span> wird gespeichert.
          </Step>
          <Step n="2" title="Vertrauenswürdigen Katalog einrichten (einmalig)">
            In Word: <em>Datei → Optionen → Trust Center → Einstellungen für das Trust Center → Vertrauenswürdige Add-In-Kataloge</em>.
            Legen Sie einen Netzwerk-Ordner oder Sharepoint-Pfad an, in dem Sie die XML ablegen. Aktivieren Sie das Häkchen „Im Menü anzeigen" und starten Sie Word neu.
            <div className="text-xs text-slate-500 mt-1">Alternativ: <em>Einfügen → Add-Ins → Add-Ins verwalten → Eigene Add-Ins hochladen</em> (Microsoft 365).</div>
          </Step>
          <Step n="3" title="Add-In sideloaden">
            <em>Einfügen → Add-Ins → Add-Ins abrufen → SHARED FOLDER</em>: Wählen Sie das Domivus-Manifest. Es erscheint <strong>Domivus Schriftverkehr</strong>.
          </Step>
          <Step n="4" title="Anmelden">
            Klicken Sie auf die Domivus-Schaltfläche in der Word-Multifunktionsleiste. Im Aufgabenbereich melden Sie sich mit Ihrem Domivus-Konto an (E-Mail + Passwort).
          </Step>
          <Step n="5" title="Schreiben verfassen">
            Suchen Sie nach Kontakt, Objekt oder Mietverhältnis. Klicken Sie auf einen Platzhalter (z. B. „Empfänger · Name") – der Wert wird an der Cursorposition eingefügt. Mit „Komplette Anschrift einfügen" wird der gesamte Anschriftenblock generiert.
          </Step>
          <Step n="6" title="Zurück in Domivus speichern">
            Wenn der Brief fertig ist, geben Sie unten im Aufgabenbereich einen Titel ein und klicken Sie auf <strong>Dokument in Domivus speichern</strong>. Das DOCX wird verknüpft mit dem ausgewählten Empfänger im Mandantenordner abgelegt.
          </Step>
        </ol>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-md p-4 text-sm text-amber-900">
        <div className="font-semibold mb-1">Voraussetzungen</div>
        <ul className="list-disc list-inside space-y-1 text-amber-800">
          <li>Microsoft Word für Microsoft 365 (Desktop oder Web)</li>
          <li>Vertrauenswürdiger Add-In-Katalog oder Microsoft-365-Admin freigegebenes Sideload</li>
          <li>HTTPS-Verbindung zur Domivus-API (bereits gegeben)</li>
        </ul>
      </div>
    </>
  );
}

function Step({ n, title, children }) {
  return (
    <li className="flex gap-3" data-testid={`addin-step-${n}`}>
      <div className="w-7 h-7 shrink-0 rounded-full bg-slate-900 text-white text-xs font-bold flex items-center justify-center">{n}</div>
      <div>
        <div className="font-semibold text-slate-900 mb-0.5">{title}</div>
        <div className="text-slate-700">{children}</div>
      </div>
    </li>
  );
}

function MigrationTab() {
  const [token, setToken] = useState(() => localStorage.getItem("domivus_migration_token") || "");
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [importMode, setImportMode] = useState("replace");
  const [importFile, setImportFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  const saveToken = (v) => {
    setToken(v);
    localStorage.setItem("domivus_migration_token", v);
  };

  const loadStatus = async () => {
    if (!token) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/migration/status`, { headers: { "X-Migration-Token": token } });
      if (!r.ok) throw new Error(`Status ${r.status}: ${await r.text()}`);
      setStatus(await r.json());
    } catch (e) {
      toast.error(`Status-Abruf fehlgeschlagen: ${e.message}`);
      setStatus(null);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { if (token) loadStatus(); /* eslint-disable-next-line */ }, []);

  const downloadDump = async () => {
    if (!token) { toast.error("Bitte Migrations-Token eintragen"); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API}/migration/dump`, { headers: { "X-Migration-Token": token } });
      if (!r.ok) throw new Error(`Status ${r.status}: ${await r.text()}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      a.href = url;
      a.download = `domivus-dump-${stamp}.json.gz`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Dump heruntergeladen — Datei liegt in deinem Downloads-Ordner.");
    } catch (e) {
      toast.error(`Download fehlgeschlagen: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const doImport = async () => {
    if (!token) { toast.error("Bitte Migrations-Token eintragen"); return; }
    if (!importFile) { toast.error("Bitte zuerst eine Datei auswählen"); return; }
    if (importMode === "replace" && !await window._domivusConfirm(
      "ACHTUNG: Im 'Ersetzen'-Modus werden ALLE bestehenden Daten dieser Domivus-Instanz vorher gelöscht. Wirklich fortfahren?"
    )) return;
    setBusy(true);
    setImportResult(null);
    try {
      const r = await fetch(`${API}/migration/restore?mode=${importMode}`, {
        method: "POST",
        headers: { "X-Migration-Token": token, "Content-Type": "application/gzip" },
        body: importFile,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || `Status ${r.status}`);
      setImportResult(data);
      await loadStatus();
      toast.success(`Import abgeschlossen: ${data.total_inserted} Datensätze eingespielt`);
    } catch (e) {
      toast.error(`Import fehlgeschlagen: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const currentHost = (typeof window !== "undefined") ? window.location.host : "";

  return (
    <>
      <div className="bg-white border border-slate-200 rounded-md p-4 sm:p-6 shadow-sm space-y-4" data-testid="migration-section">
        <div>
          <div className="text-sm font-semibold flex items-center gap-2">Daten-Übertragung Preview ↔ Produktion</div>
          <p className="text-xs text-slate-600 mt-1">
            Aktuelle Instanz: <span className="font-mono font-semibold text-slate-900">{currentHost}</span>
          </p>
        </div>

        <div>
          <Label className="text-xs">Migrations-Token</Label>
          <Input
            type="password"
            value={token}
            onChange={(e) => saveToken(e.target.value)}
            placeholder="X-Migration-Token (siehe Backend .env)"
            className="font-mono text-xs"
            data-testid="migration-token-input"
          />
          <div className="text-[11px] text-slate-500 italic mt-1">
            Wird lokal in deinem Browser gespeichert. Der gleiche Token muss in <strong>beiden</strong> Umgebungen (Preview und Produktion) gesetzt sein.
          </div>
          <Button
            variant="outline" size="sm" onClick={loadStatus} disabled={!token || busy}
            className="mt-2 gap-1" type="button" data-testid="migration-load-status"
          >
            Status laden
          </Button>
        </div>

        {status && (
          <div className="bg-slate-50/60 border border-slate-200 rounded-md p-3">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Aktueller Datenbestand · {status.db_name}</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-1 text-xs">
              {Object.entries(status.collections || {}).filter(([, v]) => v > 0).map(([k, v]) => (
                <div key={k} className="flex justify-between border-b border-slate-100 py-0.5">
                  <span className="text-slate-600">{k}</span>
                  <span className="font-semibold text-slate-900 font-mono">{v}</span>
                </div>
              ))}
            </div>
            <div className="text-xs text-slate-700 mt-2 pt-2 border-t border-slate-200">
              Gesamt: <strong className="text-slate-900">{status.total_documents}</strong> Dokumente
            </div>
          </div>
        )}
      </div>

      {/* Export */}
      <div className="bg-emerald-50/40 border border-emerald-200 rounded-md p-4 sm:p-6 shadow-sm space-y-3" data-testid="migration-export">
        <div>
          <div className="text-sm font-semibold text-emerald-900">1. Daten exportieren (aus DIESER Instanz)</div>
          <p className="text-xs text-emerald-800 mt-0.5">
            Erstellt ein komprimiertes Archiv (.json.gz) mit allen Mandanten, Kontakten, Objekten, Mietverhältnissen, Buchungen usw.
          </p>
        </div>
        <Button onClick={downloadDump} disabled={!token || busy} className="bg-emerald-700 hover:bg-emerald-800 gap-2" data-testid="migration-download-btn">
          <FileArrowDown size={16} /> {busy ? "Erstelle…" : "Dump herunterladen"}
        </Button>
      </div>

      {/* Import */}
      <div className="bg-amber-50/40 border border-amber-200 rounded-md p-4 sm:p-6 shadow-sm space-y-3" data-testid="migration-import">
        <div>
          <div className="text-sm font-semibold text-amber-900">2. Daten importieren (in DIESE Instanz)</div>
          <p className="text-xs text-amber-800 mt-0.5">
            Wähle eine zuvor heruntergeladene <code className="font-mono">.json.gz</code>-Datei aus.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 items-end">
          <div>
            <Label className="text-xs">Dump-Datei</Label>
            <input
              ref={fileInputRef}
              type="file" accept=".gz,.json,application/gzip,application/json"
              onChange={(e) => setImportFile(e.target.files?.[0] || null)}
              data-testid="migration-file-input"
              className="block w-full text-sm file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-amber-100 file:text-amber-900 file:cursor-pointer cursor-pointer"
            />
            {importFile && <div className="text-[11px] text-slate-600 mt-1">Gewählt: <strong>{importFile.name}</strong> ({Math.round(importFile.size / 1024)} KB)</div>}
          </div>
          <div>
            <Label className="text-xs">Modus</Label>
            <select
              value={importMode}
              onChange={(e) => setImportMode(e.target.value)}
              data-testid="migration-mode-select"
              className="block h-9 px-3 text-sm border border-slate-200 rounded-md bg-white w-44"
            >
              <option value="replace">Ersetzen (sicher bei leerer DB)</option>
              <option value="merge">Ergänzen (nur fehlende einfügen)</option>
            </select>
          </div>
        </div>

        <Button
          onClick={doImport}
          disabled={!token || !importFile || busy}
          className="bg-amber-700 hover:bg-amber-800 gap-2"
          data-testid="migration-import-btn"
        >
          <FileArrowUp size={16} /> {busy ? "Importiere…" : "Datei importieren"}
        </Button>

        {importResult && (
          <div className="text-xs bg-white border border-slate-200 rounded-md p-3 mt-3">
            <div className="font-semibold text-slate-900 mb-1">Import abgeschlossen ✓</div>
            <div className="text-slate-700">
              {importResult.total_inserted} Datensätze eingefügt · {importResult.total_skipped} übersprungen · Modus: <strong>{importResult.mode}</strong>
            </div>
            {(importResult.errors || []).length > 0 && (
              <details className="mt-2">
                <summary className="text-rose-700 cursor-pointer">Fehler ({importResult.errors.length})</summary>
                <pre className="text-[10px] text-rose-700 mt-1 max-h-32 overflow-auto">{importResult.errors.slice(0, 50).join("\n")}</pre>
              </details>
            )}
          </div>
        )}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-md p-4 text-sm text-blue-900">
        <div className="font-semibold mb-1">Typischer Ablauf Preview → Produktion</div>
        <ol className="list-decimal list-inside space-y-1 text-blue-800">
          <li>In der <strong>Preview</strong>-Instanz (https://property-hub-839.preview.emergentagent.com) auf „Dump herunterladen" klicken.</li>
          <li>Auf <strong>https://domivus.de</strong> (Produktion) einloggen, hierher zurückkehren.</li>
          <li>Datei auswählen → Modus <strong>„Ersetzen"</strong> → „Datei importieren".</li>
          <li>Anschließend kannst du dich mit deinen gewohnten Zugangsdaten anmelden.</li>
        </ol>
        <div className="text-xs text-blue-700 mt-2">
          Hinweis: Hochgeladene Dateien (Logos, Avatare, PDF-Briefe) werden separat im Object Storage gehalten und nicht automatisch mitübertragen.
        </div>
      </div>
    </>
  );
}

function AutoReportSection() {
  const [cfg, setCfg] = useState({ enabled: false, offset_days: 2 });
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    api.get("/owner-reports/auto/config").then(({ data }) => setCfg(data || { enabled: false, offset_days: 2 }));
  }, []);
  const save = async () => {
    setLoading(true);
    try {
      await api.put("/owner-reports/auto/config", cfg);
      toast.success("Auto-Versand gespeichert");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  const trigger = async () => {
    if (!await window._domivusConfirm("Eigentümerreport jetzt an ALLE Eigentümer dieses Mandanten versenden?")) return;
    setLoading(true);
    try {
      const { data } = await api.post("/owner-reports/auto/trigger");
      const sent = (data.results || []).filter((r) => r.status === "sent").length;
      const failed = (data.results || []).filter((r) => r.status === "failed").length;
      const queued = (data.results || []).filter((r) => r.status === "queued_no_provider").length;
      toast.success(`Versand abgeschlossen: ${sent} versendet · ${queued} vorgemerkt · ${failed} fehlgeschlagen`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4 sm:p-6 shadow-sm space-y-3" data-testid="stamm-auto-report-section">
      <div className="text-sm font-semibold flex items-center gap-2"><Buildings size={16} /> Eigentümerreport — Auto-Versand</div>
      <p className="text-xs text-slate-600">
        Wenn aktiv, wird der Eigentümerreport (PDF) automatisch <strong>X Tage nach dem Fälligkeitstermin</strong> der
        Mieten per E-Mail an jeden Eigentümer versendet. Der Auslösetag wird pro Eigentümer aus dem häufigsten
        <code className="mx-1 px-1 bg-slate-100 rounded text-[11px]">due_day</code> seiner aktiven Mietverhältnisse berechnet.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr_auto] gap-3 items-end pt-2">
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox" checked={cfg.enabled}
            onChange={(e) => setCfg((s) => ({ ...s, enabled: e.target.checked }))}
            className="w-4 h-4 accent-slate-900"
            data-testid="stamm-auto-report-enabled"
          />
          <span className="text-sm">Auto-Versand aktiviert</span>
        </label>
        <div>
          <Label className="text-xs">Tage nach Fälligkeit</Label>
          <Input
            type="number" min={0} max={15}
            value={cfg.offset_days}
            onChange={(e) => setCfg((s) => ({ ...s, offset_days: Number(e.target.value) || 0 }))}
            className="w-32"
            data-testid="stamm-auto-report-offset"
          />
        </div>
        <div className="flex gap-2">
          <Button variant="outline" type="button" onClick={trigger} disabled={loading} data-testid="stamm-auto-report-trigger">
            Jetzt versenden
          </Button>
          <Button onClick={save} disabled={loading} className="bg-slate-900 hover:bg-slate-800 gap-2" data-testid="stamm-auto-report-save">
            <FloppyDisk size={14} /> Speichern
          </Button>
        </div>
      </div>
    </div>
  );
}

function F({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-xs text-slate-700">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
